import React, { useEffect, useState } from 'react';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Shield, CheckCircle, AlertCircle, Brain } from 'lucide-react';
import { authservice } from '../api/authservice';
import { persistTenantBranding } from '../common/tenantBranding';
import { redirectBasedOnRole } from '../../routes/decodedAdmin';
import { entraSsoLoginUrl } from '../../pages/portal/configurations/AccessManagement/identityProviders/entraConnectionService';
import { persistEntraSession } from '../../pages/auth/EntraComplete';

const Cookies = require('js-cookie');

interface LoginProps {
  onLogin?: (credentials: { email: string; password: string }) => Promise<void>;
}

const Login: React.FC<LoginProps> = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [ssoProviders, setSsoProviders] = useState<Array<{ connectionId?: string; ConnectionId?: string; name?: string }>>([]);

  useEffect(() => {
    setIsVisible(true);
    authservice.listSsoProviders()
      .then((rows) => setSsoProviders(Array.isArray(rows) ? rows : []))
      .catch(() => setSsoProviders([]));
  }, []);

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const isFormValid = Boolean(email && password && isValidEmail(email));
  const busy = isLoading || ssoLoading;

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!isFormValid || busy) return;
    setError('');
    setIsLoading(true);
    try {
      const response = await authservice.login({ email, password });
      const result = response?.token;
      if (!result || result.success === false || !result.token) {
        setError(result?.message || 'Authentication failed. Please check your credentials.');
        return;
      }
      const { token, uid, fullName, email: userEmail, tenant_id, routes, name, logoUrl, firstPage } = response.token;

      localStorage.setItem('token', token);
      localStorage.setItem('user_uid', uid);
      localStorage.setItem('user_name', fullName);
      localStorage.setItem('user_email', userEmail);
      localStorage.setItem('tenant_id', tenant_id);
      persistTenantBranding(name, logoUrl);
      localStorage.setItem('user_routes', JSON.stringify(routes));
      if (firstPage) localStorage.setItem('first_page', firstPage);
      else localStorage.removeItem('first_page');

      Cookies.set('auth_token', token, { secure: true, sameSite: 'Strict' });
      Cookies.set('tenant_id', tenant_id);

      await new Promise((resolve) => setTimeout(resolve, 400));
      redirectBasedOnRole(token);
    } catch (err: any) {
      setError(
        err.response?.data?.token?.message ||
        err.response?.data?.message ||
        err.message ||
        'Authentication failed. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSso = () => {
    if (busy) return;
    setError('');
    const connectionId = ssoProviders[0]?.connectionId || ssoProviders[0]?.ConnectionId;
    const popup = window.open(entraSsoLoginUrl(connectionId, 'login'), 'entra-sso-login', 'width=520,height=720');
    if (!popup) {
      setError('Allow popups to sign in with Microsoft.');
      return;
    }

    setSsoLoading(true);
    const finish = (message?: string) => {
      setSsoLoading(false);
      if (message) setError(message);
    };

    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      const fromApp = event.origin === window.location.origin;
      if (!data) return;
      if (data.type === 'ENTRA_SSO_LOGIN' && !fromApp) return;
      if (data.type !== 'ENTRA_SSO_LOGIN' && data.type !== 'ENTRA_SSO_TEST') return;
      window.removeEventListener('message', onMessage);
      if (data.ok && data.type === 'ENTRA_SSO_LOGIN' && data.payload?.token) {
        persistEntraSession(data.payload);
        finish();
        redirectBasedOnRole(data.payload.token);
        return;
      }
      finish(data.message || 'Microsoft sign-in was cancelled or no matching Selfic user was found.');
    };
    window.addEventListener('message', onMessage);

    const timer = window.setInterval(() => {
      if (!popup.closed) return;
      window.clearInterval(timer);
      window.removeEventListener('message', onMessage);
      finish();
    }, 500);
  };

  return (
    <div className="min-h-screen bg-[#07080c] text-white flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -left-16 w-[28rem] h-[28rem] rounded-full bg-indigo-600/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[32rem] h-[32rem] rounded-full bg-cyan-500/10 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.16) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
      </div>

      <div className={`relative z-10 w-full max-w-6xl mx-auto px-6 py-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="hidden lg:block space-y-10">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <Brain className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-semibold tracking-tight">Selfic AI</h1>
                <p className="text-sm text-neutral-400">Enterprise AI Platform</p>
              </div>
            </div>

            <div className="space-y-4 max-w-lg">
              <h2 className="text-4xl font-semibold leading-tight">
                Sign in to your workspace
              </h2>
              <p className="text-neutral-400 text-lg leading-relaxed">
                Use your email and password, or continue with Microsoft if your administrator enabled single sign-on.
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium text-white flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-400" />
                Protected access
              </p>
              {['Password login for local administrators', 'Microsoft SSO for directory users'].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm text-neutral-300">
                  <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="w-full max-w-md mx-auto lg:mx-0">
            <div className="rounded-3xl border border-white/10 bg-neutral-900/70 backdrop-blur-xl p-8 shadow-2xl">
              <div className="mb-7">
                <h3 className="text-2xl font-semibold">Welcome back</h3>
                <p className="text-sm text-neutral-400 mt-1">Enter your credentials to continue</p>
              </div>

              {error && (
                <div className="mb-5 p-3.5 rounded-xl border border-red-500/20 bg-red-500/10 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              )}

              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="space-y-1.5">
                  <label htmlFor="email" className="block text-sm text-neutral-300">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-xl border border-neutral-700 bg-neutral-950/70 pl-11 pr-4 py-3 text-sm text-white placeholder:text-neutral-500 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                      placeholder="you@company.com"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="password" className="block text-sm text-neutral-300">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-xl border border-neutral-700 bg-neutral-950/70 pl-11 pr-12 py-3 text-sm text-white placeholder:text-neutral-500 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-300"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm text-neutral-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="rounded border-neutral-600 bg-neutral-900 text-indigo-500 focus:ring-indigo-500"
                  />
                  Remember me
                </label>

                <button
                  type="submit"
                  disabled={!isFormValid || busy}
                  className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition ${
                    isFormValid && !busy
                      ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
                      : 'bg-neutral-800 text-neutral-500 cursor-not-allowed'
                  }`}
                >
                  {isLoading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign in
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              <div className="my-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-neutral-800" />
                <span className="text-xs uppercase tracking-wider text-neutral-500">or</span>
                <div className="h-px flex-1 bg-neutral-800" />
              </div>

              <button
                type="button"
                onClick={handleSso}
                disabled={busy}
                className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border border-neutral-700 bg-neutral-950/60 text-sm font-medium text-white hover:bg-neutral-800 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {ssoLoading ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <MicrosoftMark />
                )}
                {ssoLoading ? 'Waiting for Microsoft...' : 'Continue with Microsoft'}
              </button>
              <p className="mt-3 text-xs text-neutral-500 text-center">
                Microsoft asks for your account in the popup. Email and password are only for local administrators.
              </p>
            </div>

            <p className="mt-6 text-center text-xs text-neutral-600">
              © 2026 Selfic AI. Secure enterprise platform.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const MicrosoftMark = () => (
  <svg viewBox="0 0 23 23" className="w-4 h-4" aria-hidden="true">
    <path fill="#f35325" d="M1 1h10v10H1z" />
    <path fill="#81bc06" d="M12 1h10v10H12z" />
    <path fill="#05a6f0" d="M1 12h10v10H1z" />
    <path fill="#ffba08" d="M12 12h10v10H12z" />
  </svg>
);

export default Login;
